import classNames from 'classnames';
import equal from 'fast-deep-equal/es6';
import { useState } from 'react';
import {
  CardValue,
  valueToRank,
  valueToWord,
} from '../../../Models/CardModels';
import {
  IDynamicRuleSetOptions,
  ISpecialCardOptions,
  SpecialCardPlayMode,
} from '../../../Models/GameSetupModels';
import {
  BUTTON_TEXT_CLASS_NAME,
  FOCUS_CLASS_NAME,
} from '../StartScreen/styles';
import SegmentedControl from '../StartScreen/SegmentedControl';
import {
  SETTING_ROW_CLASS_NAME,
  SETTINGS_LIST_CLASS_NAME,
  SettingText,
  SwitchRow,
} from './LobbyControls';
import { ranksWhere } from '../FeltMat/feltRules';

interface ISpecialCardsProps {
  labelledBy: string;
  options: IDynamicRuleSetOptions;
  onChange?: (options: IDynamicRuleSetOptions) => void;
}

type CardName = keyof typeof CardValue;

type EffectField = Exclude<keyof ISpecialCardOptions, 'playMode'>;

const CARD_VALUES = Object.values(CardValue).filter(
  (value): value is CardValue => typeof value === 'number'
);

const NORMAL_CARD: ISpecialCardOptions = {
  playMode: SpecialCardPlayMode.Unmodified,
  switchesNextPlayToDescending: false,
  glass: false,
  resetsThePile: false,
  burns: false,
};

function withArticle(word: string) {
  return `${/^[aeiou]/.test(word) ? 'an' : 'a'} ${word}`;
}

function playModes(options: IDynamicRuleSetOptions) {
  const lowerRanks = ranksWhere(
    options,
    (card) => card.switchesNextPlayToDescending
  );
  const lowerCards = CARD_VALUES.filter(
    (value) => specialCard(options, value)?.switchesNextPlayToDescending
  )
    .map((value) => withArticle(valueToWord(value)))
    .join(' or ');
  return [
    {
      value: SpecialCardPlayMode.Unmodified,
      label: 'Normally',
      description: 'Follows the pile, like any other card',
    },
    {
      value: SpecialCardPlayMode.CanAlwaysPlay,
      label: 'Always',
      description: lowerCards
        ? `On any card, even ${lowerCards}`
        : 'On any card',
    },
    {
      value: SpecialCardPlayMode.CanAlwaysPlayExceptOnDescending,
      label: lowerRanks ? `Not on ${lowerRanks}` : 'Not on lower',
      description: lowerCards
        ? `On any card except ${lowerCards}`
        : 'Same as Always, since no card switches to lower',
    },
  ];
}

const EFFECTS: { field: EffectField; title: string; description: string }[] = [
  {
    field: 'resetsThePile',
    title: 'Resets the pile',
    description: 'Anything can be played on it',
  },
  {
    field: 'burns',
    title: 'Burns the pile',
    description: 'Clears the pile, and you go again',
  },
  {
    field: 'switchesNextPlayToDescending',
    title: 'Switches to lower',
    description: 'The next card has to match it or go lower',
  },
  {
    field: 'glass',
    title: 'Glass',
    description: 'See-through, so the next card plays on the one under it',
  },
];

function cardName(value: CardValue) {
  return CardValue[value] as CardName;
}

function specialCard(options: IDynamicRuleSetOptions, value: CardValue) {
  return options.specialCards[cardName(value)];
}

function isSpecial(card: ISpecialCardOptions | undefined) {
  return !!card && !equal(card, NORMAL_CARD);
}

function capitalise(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function cardEffects(
  card: ISpecialCardOptions,
  options: IDynamicRuleSetOptions
) {
  return [
    card.playMode !== SpecialCardPlayMode.Unmodified &&
      playModes(options).find(({ value }) => value === card.playMode)
        ?.description,
    card.resetsThePile && 'Resets the pile',
    card.burns && 'Burns the pile',
    card.switchesNextPlayToDescending && 'Switches to lower',
    card.glass && 'Glass',
  ]
    .filter(Boolean)
    .join(' · ');
}

function RankChip({ value }: { value: CardValue }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-[40px] w-[32px] shrink-0 items-center justify-center rounded-[8px] bg-accent-fill text-[15px] font-bold text-accent-on"
    >
      {valueToRank(value)}
    </span>
  );
}

function SpecialCardList({
  labelledBy,
  options,
}: Omit<ISpecialCardsProps, 'onChange'>) {
  const specialValues = CARD_VALUES.filter((value) =>
    isSpecial(specialCard(options, value))
  );
  return (
    <ul aria-labelledby={labelledBy} className={SETTINGS_LIST_CLASS_NAME}>
      {specialValues.length === 0 && (
        <li className={SETTING_ROW_CLASS_NAME}>
          <SettingText title="No special cards" />
        </li>
      )}
      {specialValues.map((value) => (
        <li key={value} className={SETTING_ROW_CLASS_NAME}>
          <RankChip value={value} />
          <SettingText
            title={capitalise(valueToWord(value, true))}
            description={cardEffects(
              specialCard(options, value) ?? NORMAL_CARD,
              options
            )}
          />
        </li>
      ))}
    </ul>
  );
}

export default function SpecialCards({
  labelledBy,
  options,
  onChange,
}: ISpecialCardsProps) {
  const [selectedValue, setSelectedValue] = useState(
    () =>
      CARD_VALUES.find((value) => isSpecial(specialCard(options, value))) ??
      CardValue.TWO
  );

  if (!onChange) {
    return <SpecialCardList labelledBy={labelledBy} options={options} />;
  }

  const selectedCard = specialCard(options, selectedValue) ?? NORMAL_CARD;
  const selectedWord = valueToWord(selectedValue, true);
  const modes = playModes(options);
  const selectedMode =
    modes.find(({ value }) => value === selectedCard.playMode) ?? modes[0];

  const updateSelectedCard = (changes: Partial<ISpecialCardOptions>) => {
    const card = { ...selectedCard, ...changes };
    const specialCards = {
      ...options.specialCards,
      [cardName(selectedValue)]: card,
    };
    if (!isSpecial(card)) {
      delete specialCards[cardName(selectedValue)];
    }
    onChange({ ...options, specialCards });
  };

  return (
    <div
      aria-labelledby={labelledBy}
      role="group"
      className={SETTINGS_LIST_CLASS_NAME}
    >
      <div className="flex flex-col gap-[10px] px-[14px] py-[12px]">
        <SettingText
          title="Pick a card"
          description="Highlighted cards have special effects"
        />
        <div
          role="radiogroup"
          aria-label="Card to change"
          className="grid grid-cols-7 gap-[6px]"
        >
          {CARD_VALUES.map((value) => {
            const isSelected = value === selectedValue;
            const special = isSpecial(specialCard(options, value));
            return (
              <button
                key={value}
                role="radio"
                aria-checked={isSelected}
                aria-label={`${capitalise(valueToWord(value, true))}${special ? ', special' : ''}`}
                onClick={() => setSelectedValue(value)}
                className={classNames(
                  'flex h-[44px] items-center justify-center rounded-[10px] text-[15px] font-bold',
                  special
                    ? 'bg-accent-fill text-accent-on'
                    : 'bg-control text-ink',
                  {
                    'ring-2 ring-ink ring-offset-2 ring-offset-surface':
                      isSelected,
                  },
                  FOCUS_CLASS_NAME
                )}
              >
                <span className={BUTTON_TEXT_CLASS_NAME}>
                  {valueToRank(value)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-[8px] px-[14px] py-[12px]">
        <SettingText title={`${capitalise(selectedWord)} can be played`} />
        <SegmentedControl
          label={`When ${selectedWord} can be played`}
          options={modes}
          value={selectedCard.playMode}
          onChange={(playMode) => updateSelectedCard({ playMode })}
        />
        <span className="text-[12px] font-medium text-ink-muted">
          {selectedMode.description}
        </span>
      </div>
      <ul
        aria-label={`What ${selectedWord} do`}
        className="divide-y divide-hairline"
      >
        {EFFECTS.map(({ field, title, description }) => (
          <SwitchRow
            key={field}
            title={title}
            description={description}
            checked={selectedCard[field]}
            onChange={(checked) => updateSelectedCard({ [field]: checked })}
          />
        ))}
      </ul>
    </div>
  );
}
